import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore, $userCardsStore } from '@lib/store';
import { useListState } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { CardStatus } from '@shared/api';
import { CardItem, View } from '@widgets';
import { NoData, Pagination, Spinner, Tabs } from '@shared/widgets';

interface UserCardsProps {
  className?: string;
  userId: string;
}

export const UserCards = (props: UserCardsProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [status, setStatus] = useState<CardStatus>();

  const userCardsState = useListState({ list: 'userCards' });

  const { t } = useTranslation('user');

  useEffect(() => {
    return () => $userCardsStore.events.clear();
  }, []);

  useEffect(() => {
    $userCardsStore.events.getUserCards({
      Status: status,
      AssignedUserId: props.userId,
      FromIndex: userCardsState.fromIndex,
      Count: userCardsState.count,
    });
  }, [props.userId, status, userCardsState]);

  const statusOptions = useMemo(
    () => [
      { label: t('statuses.card.All', { ns: 'common' }), value: undefined },
      ...Object.entries(CardStatus).map(([value, label]) => ({ label: t(`statuses.card.${label}`, { ns: 'common' }), value })),
    ],
    [t],
  );
  return (
    <section className={cn(props.className)}>
      <View
        error={userCardsStore.error}
        onRefresh={() => (
          $userCardsStore.events.clear(),
          $userCardsStore.events.getUserCards({
            Status: status,
            AssignedUserId: props.userId,
            FromIndex: userCardsState.fromIndex,
            Count: userCardsState.count,
          })
        )}
      >
        <Tabs
          className={cn('w-full', PaletteUtils.isVcfcPalette && 'rounded-t-xl bg-white px-6 py-4 dark:bg-soft-black')}
          listClassName={cn(PaletteUtils.isInsigniaPalette && 'gap-0', PaletteUtils.isVcfcPalette && 'gap-12')}
          labelClassName={cn(PaletteUtils.isInsigniaPalette && 'pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black')}
          options={statusOptions}
          value={status}
          onChange={(value) => setStatus(value as CardStatus)}
        />
        {userCardsStore.loading && <Spinner className={cn('bg-white dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'rounded-b-xl py-5')} />}
        {!userCardsStore.loading && userCardsStore.data.data.length > 0 && (
          <div
            className={cn(
              'rounded-tr-2 rounded-br-2 rounded-bl-2 flex w-full flex-wrap gap-x-[26px] gap-y-[30px] rounded-tl-none bg-white px-[13px] pb-[30px] pt-5 dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && ['rounded-b-xl rounded-t-none', 'max-md:justify-center'],
            )}
          >
            {userCardsStore.data.data.map((card, idx) => {
              const viewCardDetails = profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === card.cardId)?.viewCardDetails;
              return (
                <Link key={idx} to={RoutePaths.card.onNavigate(card.cardId)} onClick={(e) => viewCardDetails === false && e.preventDefault()}>
                  <CardItem card={card} withHover />
                </Link>
              );
            })}
          </div>
        )}
        {!userCardsStore.loading && userCardsStore.data.data.length === 0 && (
          <NoData
            className={cn('bg-white py-16 dark:bg-soft-black', PaletteUtils.isVcfcPalette && 'rounded-b-xl')}
            text={t('noData.noRecords', { ns: 'common' })}
          />
        )}
        {userCardsStore.data.data.length > 0 && (
          <Pagination
            className='mt-5'
            perPageLabel={t('pagination.perPage.items', { ns: 'common' })}
            totalItemCount={userCardsStore.data.total}
            currentPage={userCardsState.page}
            countItemPage={userCardsState.count}
            onChangePage={userCardsState.onChangePage}
            onChangeCountItemPage={userCardsState.onChangeCount}
          />
        )}
      </View>
    </section>
  );
};
